import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export const saClient = axios.create({
  baseURL: API_BASE,
});

// Attach token from localStorage for SuperAdmin
saClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('sa_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export interface SuperAdminUser {
  id: number;
  email: string;
  role: string;
  isActive: boolean;
  lastLoginAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  errors?: string[];
  data?: T;
}

export interface SuperAdminLoginResponse {
  token: string;
  user: SuperAdminUser;
}

export interface TenantDomain {
  id: number;
  tenantId: number;
  hostname: string;
  isPrimary: boolean;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  currency: string;
  price: number;
  isActive: boolean;
  featuresJson?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SaPlan {
  id: number;
  code: string;
  name: string;
  price: number;
  currency: string;
  isActive: boolean;
}

export interface SaTenantRow {
  id: number;
  name: string;
  subdomain: string;
  status: string;
  isActive: boolean;
  subscriptionActive: boolean;
  plan?: string | null;
  planId?: number | null;
  planCode?: string | null;
  planPrice?: number | null;
  planCurrency?: string | null;
  adminEmail?: string | null;
  adminPhone?: string | null;
  billingEmail?: string | null;
  adminName?: string | null;
  adminUserEmail?: string | null;
  usersCount: number;
  lastLoginAt?: string | null;
  productsCount: number;
  orders30: number;
  revenue30: number;
  revenue365: number;
  lastOrderAt?: string | null;
  demoEndsAt?: string | null;
  subscriptionEndsAt?: string | null;
  nextPaymentDate?: string | null;
  accessEndsAt?: string | null;
  lastPaymentAt?: string | null;
  lastPaymentAmount?: number | null;
  lastPaymentCurrency?: string | null;
  paymentsCount: number;
  paidVia?: string | null;
  invoicingTier?: string | null;
  mercadoPagoConnected: boolean;
  enabledFlags: string[];
  country?: string | null;
  preferredPaymentProvider?: string | null;
  businessKind?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  excludeFromMetrics: boolean;
  statusReason?: string | null;
  superAdminNotes?: string | null;
  createdAt: string;
}

export const superAdminApi = {
  login: async (email: string, password: string) => {
    const { data } = await saClient.post<ApiResponse<SuperAdminLoginResponse>>('/super-admin/auth/login', { email, password });
    return data;
  },
  getTenants: async () => {
    const { data } = await saClient.get<ApiResponse<any[]>>('/super-admin/tenants');
    return data;
  },
  getTenant: async (id: number) => {
    const { data } = await saClient.get<ApiResponse<any>>(`/super-admin/tenants/${id}`);
    return data;
  },
  updateTenant: async (id: number, payload: any) => {
    const { data } = await saClient.put<ApiResponse<any>>(`/super-admin/tenants/${id}`, payload);
    return data;
  },
  createTenant: async (payload: any) => {
    const { data } = await saClient.post<ApiResponse<any>>('/super-admin/tenants', payload);
    return data;
  },
  deleteTenant: async (id: number) => {
    const { data } = await saClient.delete<ApiResponse<any>>(`/super-admin/tenants/${id}`);
    return data;
  },
  impersonate: async (id: number) => {
    const { data } = await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/impersonate`);
    return data;
  },
  action: async (id: number, action: 'activate'|'deactivate'|'suspend'|'resume') => {
    const { data } = await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/${action}`);
    return data;
  },
  getFeatureFlags: async (id: number) => {
    const { data } = await saClient.get<ApiResponse<any>>(`/super-admin/tenants/${id}/feature-flags`);
    return data;
  },
  updateFeatureFlags: async (id: number, flags: Array<{ key: string; enabled: boolean }>) => {
    const { data } = await saClient.put<ApiResponse<any>>(`/super-admin/tenants/${id}/feature-flags`, flags);
    return data;
  },
  getTenantDomains: async (tenantId: number) => {
    const { data } = await saClient.get<ApiResponse<TenantDomain[]>>(`/super-admin/tenants/${tenantId}/domains`);
    return data;
  },
  createTenantDomain: async (tenantId: number, payload: { hostname: string; isPrimary?: boolean }) => {
    const { data } = await saClient.post<ApiResponse<TenantDomain>>(`/super-admin/tenants/${tenantId}/domains`, payload);
    return data;
  },
  deleteTenantDomain: async (tenantId: number, domainId: number) => {
    const { data } = await saClient.delete<ApiResponse<any>>(`/super-admin/tenants/${tenantId}/domains/${domainId}`);
    return data;
  },
  setPrimaryTenantDomain: async (tenantId: number, domainId: number) => {
    const { data } = await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${tenantId}/domains/${domainId}/primary`);
    return data;
  },
  listInvitations: async (id: number) => {
    const { data } = await saClient.get<ApiResponse<any>>(`/super-admin/tenants/${id}/invitations`);
    return data;
  },
  createInvitation: async (id: number, payload: { email: string; role?: string; expiresAt?: string }) => {
    const { data } = await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/invitations`, payload);
    return data;
  },
  getSubscription: async (id: number) => {
    const { data } = await saClient.get<ApiResponse<any>>(`/super-admin/tenants/${id}/subscription`);
    return data;
  },
  updateSubscription: async (id: number, payload: any) => {
    const { data } = await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/subscription`, payload);
    return data;
  },
  getDashboardStats: async () => {
    const { data } = await saClient.get<ApiResponse<any>>('/super-admin/dashboard/stats');
    return data;
  },
  getTenantsAnalytics: async () => {
    const { data } = await saClient.get<ApiResponse<any[]>>('/super-admin/tenants/analytics');
    return data;
  },
  getRevenue: async (params?: { fromDate?: string; toDate?: string }) => {
    const { data } = await saClient.get<ApiResponse<number>>('/super-admin/revenue', { params });
    return data;
  },
  listPlans: async () => {
    const { data } = await saClient.get<ApiResponse<SubscriptionPlan[]>>('/subscription/plans/admin');
    return data;
  },
  createPlan: async (payload: SubscriptionPlan) => {
    const { data } = await saClient.post<ApiResponse<SubscriptionPlan>>('/subscription/plans/admin', payload);
    return data;
  },
  updatePlan: async (id: number, payload: SubscriptionPlan) => {
    const { data } = await saClient.put<ApiResponse<SubscriptionPlan>>(`/subscription/plans/admin/${id}`, payload);
    return data;
  },
  deletePlan: async (id: number) => {
    const { data } = await saClient.delete<ApiResponse<any>>(`/subscription/plans/admin/${id}`);
    return data;
  },
  // ==================== Pestaña Tenants (port de GymHero) ====================
  getTenantsOverview: async () => {
    const { data } = await saClient.get<ApiResponse<SaTenantRow[]>>('/super-admin/tenants/overview');
    return data.data || [];
  },
  getPlansForTenants: async () => {
    const { data } = await saClient.get<ApiResponse<SaPlan[]>>('/super-admin/plans');
    return data.data || [];
  },
  manualPayment: async (id: number, payload: {
    paymentDate?: string; periodStartDate?: string; overrideFromPaymentDate: boolean; months: number;
    planId?: number; amount?: number; method?: string; reference?: string; notes?: string;
  }) => (await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/manual-payment`, payload)).data,
  changePlan: async (id: number, payload: { planId: number; months: number; effectiveDate?: string; reason?: string }) =>
    (await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/change-plan`, payload)).data,
  extendDemo: async (id: number, days: number) =>
    (await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/extend-demo`, { days })).data,
  lifecycle: async (id: number, action: 'suspend' | 'cancel' | 'activate' | 'renew', reason?: string) =>
    (await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/lifecycle/${action}`, { reason: reason || null })).data,
  setExcludeFromMetrics: async (id: number, exclude: boolean) =>
    (await saClient.post<ApiResponse<any>>(`/super-admin/tenants/${id}/exclude-from-metrics`, { exclude })).data,
  updateTenantProfile: async (id: number, payload: Partial<Record<'adminEmail' | 'adminPhone' | 'billingEmail' | 'primaryColor' | 'secondaryColor' | 'country' | 'preferredPaymentProvider', string>>) =>
    (await saClient.patch<ApiResponse<any>>(`/super-admin/tenants/${id}/profile`, payload)).data,
  setPasswordByEmail: async (payload: { email: string; newPassword: string; userType: 'Admin' | 'SuperAdmin'; tenantId?: number }) =>
    (await saClient.post<ApiResponse<any>>('/super-admin/users/set-password-by-email', payload)).data,
  sendPasswordReset: async (email: string) =>
    (await saClient.post<ApiResponse<any>>('/super-admin/users/send-password-reset', { email })).data,
  getAccessInfo: async (id: number) =>
    (await saClient.get<ApiResponse<{ tenantName: string; adminName: string; adminEmail: string; adminPhone: string; loginUrl: string }>>(`/super-admin/tenants/${id}/access-info`)).data.data!,
  getWhatsAppReminder: async (id: number) =>
    (await saClient.get<ApiResponse<{ message: string; phoneNumber: string }>>(`/super-admin/tenants/${id}/whatsapp-reminder`)).data.data!,
};
